/**
 * Centralized Model Icon Mapping System
 *
 * This file provides a centralized location for all model type icons used across the application.
 * It ensures consistency between different parts of the app like:
 * - Activity Logs
 * - Global Search
 * - Sidebar Navigation
 * - Model Cards
 *
 * Usage:
 * - getModelIcon(modelType) - Returns React element with icon
 * - getModelLabel(modelType) - Returns human-readable label
 * - getModelIconConfig(modelType) - Returns full config object
 */

import React from 'react';
import {
  Person as PersonIcon,
  ManageAccounts as ManageAccountsIcon,
  Apartment as ApartmentIcon,
  AccountBalance as AccountBalanceIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Description as DescriptionIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';

export type ModelType =
  | 'CONTACT'
  | 'ADMIN_USER'
  | 'INVESTOR'
  | 'FUND_OPPORTUNITY'
  | 'INVESTMENT_ENTITY'
  | 'EOI'
  | 'ALLOCATION'
  | 'GLOBAL_CONFIG'
  | 'USER'
  | 'CLIENT_EXCEPTION';

export interface ModelIconConfig {
  icon: React.ComponentType<any>;
  label: string;
  color?: string;
}

export const modelIconMapping: Record<ModelType, ModelIconConfig> = {
  CONTACT: {
    icon: PersonIcon,
    label: 'Contact',
  },
  ADMIN_USER: {
    icon: ManageAccountsIcon,
    label: 'Admin User',
  },
  INVESTOR: {
    icon: AccountBalanceWalletIcon,
    label: 'Investor',
  },
  FUND_OPPORTUNITY: {
    icon: ApartmentIcon,
    label: 'Fund Opportunity',
  },
  INVESTMENT_ENTITY: {
    icon: AccountBalanceIcon, // bank-looking icon for investment entities
    label: 'Investment Entity',
  },
  EOI: {
    icon: DescriptionIcon,
    label: 'Expression of Interest',
  },
  ALLOCATION: {
    icon: AssignmentIcon,
    label: 'Allocation',
  },
  GLOBAL_CONFIG: {
    icon: SettingsIcon,
    label: 'Global Configuration',
  },
  USER: {
    icon: PersonIcon,
    label: 'User',
  },
  CLIENT_EXCEPTION: {
    icon: BugReportIcon,
    label: 'Client Exception',
  },
};

export const getModelIcon = (modelType: string): React.ReactElement => {
  const config = modelIconMapping[modelType as ModelType];
  if (!config) {
    return <AssignmentIcon />;
  }
  return <config.icon />;
};

export const getModelLabel = (modelType: string): string => {
  const config = modelIconMapping[modelType as ModelType];
  return config?.label || modelType;
};

export const getModelIconConfig = (modelType: string): ModelIconConfig | null => {
  return modelIconMapping[modelType as ModelType] || null;
};
